import { useCallback, useMemo, useState } from "react";
import { Badge, Flex, Table, Text, TextField } from "@radix-ui/themes";
import { Search } from "lucide-react";

import { Loader } from "@/components/ui/loader";
import { RegressionTestResultDetail } from "@/v2/entities/regression/Regression.model";
import { getSimilarityColor } from "./utils";
import styles from "./styles.module.scss";

interface QuestionsViewProps {
  testResults: RegressionTestResultDetail[] | undefined;
  isLoading: boolean;
  selectedResultId: string | null;
  onSelectResult: (id: string) => void;
}

function getResultText(result: RegressionTestResultDetail): string {
  return (
    result.regressionSetItem?.releaseConversation?.releaseComments?.[0]
      ?.comment ||
    result.regressionSetItem?.question ||
    "No question"
  );
}

export function QuestionsView({
  testResults,
  isLoading,
  selectedResultId,
  onSelectResult,
}: QuestionsViewProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const validResults = useMemo(
    () => (testResults ?? []).filter((r) => r.regressionSetItem != null),
    [testResults]
  );

  const filteredResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return validResults;
    return validResults.filter((result) =>
      getResultText(result).toLowerCase().includes(query)
    );
  }, [validResults, searchQuery]);

  return (
    <>
      <div className={styles.panelHeader}>
        <Flex align="center" gap="2">
          <Text size="3" weight="medium">
            Results
          </Text>
          <Badge color="gray">{validResults.length}</Badge>
        </Flex>
      </div>

      <div className={styles.panelContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        ) : validResults.length > 0 ? (
          <>
            <div className={styles.searchContainer}>
              <TextField.Root
                placeholder="Search results..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="2"
              >
                <TextField.Slot>
                  <Search size={14} />
                </TextField.Slot>
              </TextField.Root>
            </div>
            {filteredResults.length > 0 ? (
              <Table.Root className={styles.questionsTable}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell
                      className={`${styles.tableHeader} ${styles.similarityCell}`}
                    >
                      Similarity
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell
                      className={`${styles.tableHeader} ${styles.questionCell}`}
                    >
                      Conversation
                    </Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredResults.map((result) => (
                    <Table.Row
                      key={result.id}
                      className={`${styles.questionRow} ${
                        selectedResultId === result.id
                          ? styles.selectedRow
                          : ""
                      }`}
                      onClick={() => onSelectResult(result.id)}
                    >
                      <Table.Cell className={styles.similarityCell}>
                        <Badge
                          size="1"
                          color={getSimilarityColor(result.similarity)}
                        >
                          {Math.round(result.similarity * 100)}%
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className={styles.questionCell}>
                        <Text size="2" className={styles.questionText}>
                          {getResultText(result)}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <div className={styles.emptyState}>
                <Text size="2" color="gray">
                  No results matching &quot;{searchQuery}&quot;
                </Text>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <Text size="2" color="gray">
              No results available
            </Text>
          </div>
        )}
      </div>
    </>
  );
}
